import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { UserParticipationType } from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { useManageFeatureInstanceDetails } from "entity-app/hooks/feature-details-page";
import { bidPackageLabel } from "pages/planning-workspace/bid-package-label";
import { bidPackageDetailsPath } from "pages/planning-workspace/bid-package-path";

/** Width of the panel where it takes the Required on site column's place. */
export const BID_DETAIL_PANEL_W = 340;

type Risk = "HIGH" | "MEDIUM" | "LOW";

function riskOf(raw: unknown): Risk | null {
  const level = typeof raw === "string" ? raw.trim().toUpperCase() : "";
  return level === "HIGH" || level === "MEDIUM" || level === "LOW"
    ? level
    : null;
}

const RISK_TAG: Record<Risk, { label: string; className: string }> = {
  HIGH: {
    label: "High",
    className: "border-ciq-red-200 bg-ciq-red-50 text-ciq-red-600"
  },
  MEDIUM: {
    label: "Medium",
    className: "border-ciq-orange-300 bg-ciq-orange-50 text-ciq-orange-750"
  },
  LOW: {
    label: "Low",
    className: "border-ciq-blue-300 bg-ciq-blue-50 text-ciq-blue-600"
  }
};

function leadName(data: any): string {
  const lead = data?.feature_user_paticipants?.find(
    (user: any) =>
      user?.user_participation_type_id ===
      UserParticipationType.PRECONSTRUCTION_LEAD
  );
  const name = [lead?.user?.first_name, lead?.user?.last_name]
    .filter(Boolean)
    .join(" ");
  return name || "—";
}

function Field(props: {
  label: string;
  /** Sits at the right end of the label row. */
  extra?: ReactNode;
  children: ReactNode;
}) {
  const { label, extra, children } = props;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium leading-4 text-ciq-planner-ink3">
          {label}
        </span>
        {extra}
      </div>
      {children}
    </div>
  );
}

const VALUE = "text-sm font-semibold leading-[22px] text-ciq-planner-ink";

function floatText(days: number | null | undefined): string {
  if (days == null) return "—";
  const value = Math.round(days) || 0;
  return `${value} ${Math.abs(value) === 1 ? "Day" : "Days"}`;
}

/** The selected bid package's details, shown inside the design package card
 *  in place of Required on site while a bid card is selected. */
export default function PlannerBidDetailDrawer(props: {
  bidPackageId: string;
  scopeCount: number;
  label: string;
  milestoneName?: string;
  /** Whole days of float, as the timeline row reports it. */
  effectiveFloat?: number | null;
  /** Links the title to the Bid Package details page. */
  projectId?: string;
  onOpenScope?: (b: {
    bidPackageId: string;
    label: string;
    milestoneName?: string;
  }) => void;
  /** The X: hides the panel but leaves the bid package selected. */
  onHide: () => void;
}) {
  const {
    bidPackageId,
    scopeCount,
    label,
    milestoneName,
    effectiveFloat,
    projectId,
    onOpenScope,
    onHide
  } = props;
  const { featureInstanceData } = useManageFeatureInstanceDetails(
    bidPackageId,
    FeatureTypes.BID_PACKAGE
  );
  const data = featureInstanceData;
  const title = bidPackageLabel(data?.number, data?.title) || label;
  const risk = riskOf(data?.feature_workflow_instance?.risk_level);
  const status = data?.feature_workflow_instance?.milestone_state?.trim() || "";
  const wave = data?.feature_bid_package?.wave;
  const detailsPath = projectId
    ? bidPackageDetailsPath(projectId, bidPackageId)
    : null;

  return (
    <div
      data-testid="planner-bid-detail"
      className="flex h-full flex-col border-0 border-l border-solid border-ciq-gray-150 bg-white"
      style={{ width: BID_DETAIL_PANEL_W }}
    >
      <div className="flex h-[38px] shrink-0 items-center justify-between gap-3 border-0 border-b border-solid border-ciq-gray-150 px-4">
        <span className="text-sm font-medium leading-5 text-ciq-planner-ink">
          Bid Package Details
        </span>
        <button
          type="button"
          aria-label="Close"
          data-testid="planner-bid-detail-close"
          className="flex shrink-0 cursor-pointer border-0 bg-transparent p-0 text-base leading-none text-ciq-planner-ink3 hover:text-ciq-planner-ink"
          onClick={onHide}
        >
          <CloseOutlined />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!data ? (
          <p className="px-4 text-sm text-ciq-planner-ink3">
            Loading Bid Package…
          </p>
        ) : (
          <div className="flex flex-col gap-5 border-0 border-b border-solid border-ciq-gray-150 px-4 pb-4 pt-5">
            <Field label="Title">
              {detailsPath ? (
                <Link
                  to={detailsPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="planner-bid-detail-title"
                  title="Open bid package details"
                  className={`${VALUE} break-words hover:text-ciq-blue-600 hover:underline hover:underline-offset-2`}
                >
                  {title}
                </Link>
              ) : (
                <span
                  data-testid="planner-bid-detail-title"
                  className={`${VALUE} break-words`}
                >
                  {title}
                </span>
              )}
            </Field>
            <Field
              label="Status"
              extra={
                risk ? (
                  <span
                    data-testid="planner-bid-detail-risk"
                    className={`rounded border border-solid px-1.5 text-[10px] leading-4 ${RISK_TAG[risk].className}`}
                  >
                    {RISK_TAG[risk].label}
                  </span>
                ) : null
              }
            >
              <span className={VALUE}>{status || "—"}</span>
            </Field>
            <Field label="Float">
              <span data-testid="planner-bid-detail-float" className={VALUE}>
                {floatText(effectiveFloat)}
              </span>
            </Field>
            <Field label="Description">
              <p className="m-0 whitespace-pre-line break-words text-sm leading-[22px] text-ciq-planner-ink2">
                {data.description?.trim() || "—"}
              </p>
            </Field>
            <button
              type="button"
              data-testid="planner-bid-detail-scope"
              className="inline-flex w-fit cursor-pointer items-center gap-2 self-start whitespace-nowrap rounded-md border border-solid border-ciq-gray-300 bg-white px-4 py-1.5 text-sm leading-5 text-ciq-planner-accent hover:bg-ciq-primary-bg disabled:cursor-default disabled:text-ciq-gray-400"
              disabled={!onOpenScope}
              onClick={() =>
                onOpenScope?.({
                  bidPackageId,
                  label: title,
                  milestoneName
                })
              }
            >
              {scopeCount} Scope Item{scopeCount === 1 ? "" : "s"}
              <RightOutlined className="text-xs text-ciq-planner-accent" />
            </button>
            <Field label="Preconstruction Lead">
              <span className={VALUE}>{leadName(data)}</span>
            </Field>
            <Field label="Wave">
              <span className={VALUE}>
                {wave == null || wave === "" ? "—" : String(wave)}
              </span>
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}
